import React, { useMemo } from 'react';
import { RegistrationJourney, StepProps } from '../../../types/RegistrationJourney';
import {
   API_ENDPOINTS,
   FormInput,
   ImageType,
   route,
   SelectOption,
   Stepper,
   StringHelpers,
   SubTitle,
   SummaryLineItems,
   useLangContext,
   useQuery,
} from 'carrier-fe';
import { STEPS } from '../../../util/Constants';

function Step5({ form, pageLang, loading }: StepProps) {
   const { crud, fields } = useLangContext();

   const { data: countries } = useQuery<{ data: SelectOption[] }>(
      route(API_ENDPOINTS.GENERIC.SELECT.COUNTRIES)
   );

   const summaryConf = useMemo(() => {
      return summaryConfig(form?.store, { fields, crud }, countries?.data);
   }, [crud, fields, form?.store, countries?.data]);
   return (
      <Stepper.Step
         loading={loading}
         title={pageLang?.page?.steps?.[STEPS.STEP_6]?.title || 'Summary'}
         description={pageLang?.page?.steps?.[STEPS.STEP_6]?.description || ''}
      >
         <div className="d-flex flex-column gap-4">
            <div className="d-flex flex-column gap-3">
               {summaryConf?.map((item, index) => (
                  <SummaryLineItems
                     maxPerView={3}
                     key={index}
                     title={item.title}
                     items={item.items}
                  />
               ))}
            </div>
            <div>
               <SubTitle
                  title={StringHelpers.title(crud?.sub_titles?.confirmation || 'Confirmation')}
                  className="mb-3 w-100"
                  style={{ color: '#464C5E' }}
               />
               <FormInput
                  name="confirm"
                  label={StringHelpers.sentence(
                     fields?.confirmed ||
                        'Do you confirm that the details provided above are correct?'
                  )}
                  type="select"
                  required
                  value={form.store['confirmed']}
                  onChange={(value: string | boolean | string[] | ImageType | null) =>
                     form?.setValues({
                        confirmed: typeof value === 'string' ? JSON.parse(value) : value,
                     })
                  }
                  options={crud?.options?.boolean.map((option: SelectOption) => ({
                     ...option,
                     value: Number(option.value) === 1 ? true : false,
                  }))}
                  errorMessages={form.errors?.['confirmed']}
               />
            </div>
         </div>
      </Stepper.Step>
   );
}

export default Step5;

type SummaryItems = React.ComponentProps<typeof SummaryLineItems>['items'];

const summaryConfig = (
   data: RegistrationJourney,
   lang: { crud: any; fields: any },
   countries: SelectOption[] = []
): { title: string; items: SummaryItems }[] => {

   const getCountryNameFromIso3 = (countryCodeIso3: string) => {
      return countries.find((country) => 
         country.value === countryCodeIso3
      )?.label
   };

   return [
      {
         title: `${StringHelpers.sentence(lang.crud?.sub_titles?.step || 'Step')} 01`,
         items: [
            {
               label: StringHelpers.sentence(lang.fields.registered_by || 'Registered By'),
               value: StringHelpers.sentence(data.registered_by_name || '-'),
            },
         ],
      },
      {
         title: `${StringHelpers.sentence(lang.crud?.sub_titles?.step || 'Step')} 02`,
         items: [
            {
               label: StringHelpers.sentence(lang.fields.system_type || 'System Type'),
               value: StringHelpers.sentence(data.system_type_display || '-'),
            },
            {
               label: StringHelpers.sentence(lang.fields.completion_date || 'Completion Date'),
               value: StringHelpers.sentence(data.completion_date || '-'),
            },
            {
               label: StringHelpers.sentence(lang.fields.serial_number || 'Serial Number'),
               value: StringHelpers.sentence(data.serial_number || '-'),
            },
            {
               label: StringHelpers.sentence(lang?.crud?.models?.installation + ' ' +  lang?.crud?.sub_titles?.address),
               value: [
                  {
                     label: StringHelpers.sentence(lang.fields.address_line_1 || 'Address Line 1'),
                     value: StringHelpers.title(data.address_line_1 || '-'),
                  },
                  {
                     label: StringHelpers.sentence(lang.fields.town_city || 'Town/City'),
                     value: StringHelpers.title(data.town_city || '-'),
                  },
                  {
                     label: StringHelpers.sentence(
                        lang.fields.postcode_zipcode || 'Postcode/Zipcode'
                     ),
                     value: StringHelpers.upper(data.postcode_zipcode || '-'),
                  },
                  {
                     label: StringHelpers.sentence(lang.fields.state_county || 'State/County'),
                     value: StringHelpers.title(data.state_county || '-'),
                  },
                  {
                     label: StringHelpers.sentence(
                        lang.fields.sub_country_name || 'Sub Country Name'
                     ),
                     value: StringHelpers.title(data.sub_country_name || '-'),
                  },
                  {
                     label: StringHelpers.sentence(
                        lang.fields.country_code_iso_3 || 'Country'
                     ),
                     value: getCountryNameFromIso3(data.country_code_iso_3) || '-',
                  },
               ],
            },
         ],
      },
      {
         title: `${StringHelpers.sentence(lang.crud?.sub_titles?.step || 'Step')} 03`,
         items: [
            {
               label: StringHelpers.sentence(lang.fields.contact_type || 'Contact Type'),
               value: StringHelpers.sentence(data.contact_type || '-'),
            },
            {
               label: StringHelpers.sentence(lang.fields.first_name || 'First Name'),
               value: StringHelpers.title(data.first_name || '-'),
            },
            {
               label: StringHelpers.sentence(lang.fields.last_name || 'Last Name'),
               value: StringHelpers.title(data.last_name || '-'),
            },
            {
               label: StringHelpers.sentence(lang.fields.email || 'Email'),
               value: data.email || '-',
            },
            {
               label: StringHelpers.sentence(lang.fields.landline_phone_number || 'Landline Phone'),
               value: StringHelpers.sentence(
                  `${data.landline_phone_prefix || ''} ${data.landline_phone_number || '-'}`
               ),
            },
            {
               label: StringHelpers.sentence(lang.fields.mobile_phone_number || 'Mobile Phone'),
               value: StringHelpers.sentence(
                  `${data.mobile_phone_prefix || ''} ${data.mobile_phone_number || '-'}`
               ),
            },
         ],
      },
      {
         title: `${StringHelpers.sentence(lang.crud?.sub_titles?.step || 'Step')} 04`,
         items: [
            {
               label: StringHelpers.sentence(lang.fields.company_name || 'Company Name'),
               value: StringHelpers.sentence(data.company_name || '-'),
            },
            {
               label: StringHelpers.sentence(`${lang?.crud?.sub_titles?.company} ${lang?.crud?.sub_titles?.address}`),
               value: [
                  {
                     label: StringHelpers.sentence(
                        lang.fields.address_line_1 || 'Address Line 1'
                     ),
                     value: StringHelpers.title(data.company_address_line_1 || '-'),
                  },
                  {
                     label: StringHelpers.sentence(
                        lang.fields.address_line_2 || 'Address Line 2'
                     ),
                     value: StringHelpers.title(data.company_address_line_2 || '-'),
                  },
                  {
                     label: StringHelpers.sentence(
                        lang.fields.address_line_3 || 'Address Line 3'
                     ),
                     value: StringHelpers.title(data.company_address_line_3 || '-'),
                  },
                  {
                     label: StringHelpers.sentence(
                        lang.fields.town_city || 'Town/City'
                     ),
                     value: StringHelpers.title(data.company_town_city || '-'),
                  },
                  {
                     label: StringHelpers.sentence(
                        lang.fields.postcode_zipcode || 'Postcode/Zipcode'
                     ),
                     value: StringHelpers.upper(data.company_postcode_zipcode || '-'),
                  },
                  {
                     label: StringHelpers.sentence(
                        lang.fields.state_county || 'State/County'
                     ),
                     value: StringHelpers.sentence(data.company_state_county || '-'),
                  },
                  {
                     label: StringHelpers.sentence(
                        lang.fields.sub_country_name || 'Sub Country Name'
                     ),
                     value: StringHelpers.title(data.company_sub_country_name || '-'),
                  },
                  {
                     label: StringHelpers.sentence(
                        lang.fields.country_code_iso_3 || 'Country'
                     ),
                     value: getCountryNameFromIso3(data.company_country_code_iso_3) || '-',
                  },
               ],
            },
            {
               label: StringHelpers.sentence(
                  lang.fields.company_gas_safe_number || 'Company Gas Safe Number'
               ),
               value: StringHelpers.sentence(data.company_gas_safe_number || '-'),
            },
         ],
      },
   ];
};
